import type { WebPaymentStatus } from './api';
/**
 * Web sitesi bölümünün gezinme durumu (taslak A, 07.10.2026).
 *
 * Taslakta her sayı ve grafik "ilgili sekmeyi süzülmüş açıyor". Bu dosya o davranışın tamamı:
 * bir hedef metni (`talepler:destek`, `stage:sip:bekleyen`, `risk-c`...) durumu nasıl değiştirir.
 * Ekranda işlev yok, yalnız saf durum; bu yüzden tek başına denenebiliyor
 * (`scripts/web-sitesi-durum.test.mjs`).
 */

export type Sekme = 'genel' | 'talepler' | 'uyeler' | 'siparisler' | 'odemeler';

export interface TalepSuzgeci {
  tab: 'sende' | 'musteri' | 'kapali';
  tur: 'hepsi' | 'destek' | 'on' | 'vize' | 'onvize' | 'sistem' | 'spam';
  over: boolean;
  q: string;
  from: string;
  to: string;
  sort: 'yas' | 'yeni';
  adet: number;
}

export interface UyeSuzgeci {
  f: 'hepsi' | 'dogrulanmamis' | 'dogrulanmis' | 'oturumsuz' | 'siparisli' | 'kayip-odemeli';
  q: string;
  from: string;
  to: string;
  sort: 'reg' | 'seen';
  adet: number;
}

export interface SiparisSuzgeci {
  f: 'hepsi' | 'bekleyen' | 'rez' | 'geciken' | 'odendi' | 'iptal';
  q: string;
  from: string;
  to: string;
  who: string | null;
  adet: number;
}

export interface OdemeSuzgeci { reference: string; status: WebPaymentStatus | ''; notification: 'failed' | ''; from: string; to: string; offset: number }

export interface Durum {
  sekme: Sekme;
  /** Açık kişi sayfası: v3 üye kimliği. */
  kisi: string | null;
  /** Kişi sayfası "karar ver" ile açıldıysa karar kartına kaydır. */
  kararaGit: boolean;
  pf: string;
  talep: TalepSuzgeci;
  uye: UyeSuzgeci;
  sip: SiparisSuzgeci;
  odeme: OdemeSuzgeci;
}

export const SAYFA = 50;

export function ilkDurum(): Durum {
  return {
    sekme: 'genel',
    odeme: { reference: '', status: '', notification: '', from: '', to: '', offset: 0 },
    kisi: null,
    kararaGit: false,
    pf: 'hepsi',
    talep: { tab: 'sende', tur: 'hepsi', over: false, q: '', from: '', to: '', sort: 'yas', adet: SAYFA },
    uye: { f: 'hepsi', q: '', from: '', to: '', sort: 'reg', adet: SAYFA },
    sip: { f: 'hepsi', q: '', from: '', to: '', who: null, adet: SAYFA },
  };
}

const bos = { q: '', from: '', to: '', adet: SAYFA } as const;

/** Bir sekmeye gidiş: kişi sayfası kapanır, kişi süzgeci sıfırlanır. */
function sekmeAc(d: Durum, sekme: Sekme): Durum {
  return { ...d, sekme, kisi: null, kararaGit: false, pf: 'hepsi' };
}

/**
 * Bir hedefe git. Hedef metinleri taslaktakiyle aynı:
 *
 * - `tab:<sekme>`, `uyeler:reg|seen`, `talepler:<tür>`, `siparis:hepsi`
 * - `sende` (açık işler), `must` (müşteride, yani ödeme bekleyen siparişler)
 * - `stage:<kaynak>:<satır>`, `funnel:<adım>`, `risk-a|b|c|d`
 * - `person:<kimlik>[:karar]`, `pord:<kimlik>`
 */
export function git(d: Durum, hedef: string): Durum {
  const p = hedef.split(':');
  const k = p[0];
  if (k === 'tab') return sekmeAc(d, (p[1] as Sekme) ?? 'genel');
  if (k === 'uyeler') return sekmeAc({ ...d, uye: { ...d.uye, f: 'hepsi', ...bos, sort: p[1] === 'seen' ? 'seen' : 'reg' } }, 'uyeler');
  if (k === 'talepler') return sekmeAc({ ...d, talep: { ...d.talep, tab: 'sende', tur: (p[1] as TalepSuzgeci['tur']) ?? 'hepsi', over: false, ...bos } }, 'talepler');
  if (k === 'siparis') return sekmeAc({ ...d, sip: { ...d.sip, f: 'hepsi', ...bos, who: null } }, 'siparisler');
  if (hedef === 'sende') return sekmeAc({ ...d, talep: { ...d.talep, tab: 'sende', tur: 'hepsi', over: false, ...bos } }, 'talepler');
  if (hedef === 'must') return sekmeAc({ ...d, sip: { ...d.sip, f: 'bekleyen', ...bos, who: null } }, 'siparisler');
  if (k === 'stage') {
    const kaynak = p[1];
    const satir = p[2] ?? '';
    if (kaynak === 'sip') {
      const f = (['hepsi', 'bekleyen', 'odendi', 'iptal'] as const).find((x) => x === satir) ?? 'hepsi';
      return sekmeAc({ ...d, sip: { ...d.sip, f, ...bos, who: null } }, 'siparisler');
    }
    const tur = kaynak === 'destek' ? 'destek' : kaynak === 'vize' ? 'vize' : 'on';
    const tab: TalepSuzgeci['tab'] =
      satir === 'cevap' || satir === 'islendi' ? 'musteri' : satir === 'kapali' || satir === 'cikti' || satir === 'red' ? 'kapali' : 'sende';
    return sekmeAc({ ...d, talep: { ...d.talep, tab, tur, over: false, ...bos } }, 'talepler');
  }
  if (k === 'funnel') {
    const i = Number(p[1]);
    if (i <= 1) return sekmeAc({ ...d, uye: { ...d.uye, f: i === 1 ? 'dogrulanmis' : 'hepsi', ...bos, sort: 'reg' } }, 'uyeler');
    return sekmeAc({ ...d, sip: { ...d.sip, f: i === 2 ? 'hepsi' : 'odendi', ...bos, who: null } }, 'siparisler');
  }
  if (hedef === 'risk-a') return sekmeAc({ ...d, uye: { ...d.uye, f: 'kayip-odemeli', ...bos, sort: 'seen' } }, 'uyeler');
  if (hedef === 'risk-b') return sekmeAc({ ...d, uye: { ...d.uye, f: 'dogrulanmamis', ...bos, sort: 'reg' } }, 'uyeler');
  if (hedef === 'risk-c') return sekmeAc({ ...d, sip: { ...d.sip, f: 'geciken', ...bos, who: null } }, 'siparisler');
  if (hedef === 'risk-d') return sekmeAc({ ...d, talep: { ...d.talep, tab: 'sende', tur: 'hepsi', over: true, ...bos } }, 'talepler');
  if (k === 'person') return { ...d, sekme: 'uyeler', kisi: p.slice(1, p[p.length - 1] === 'karar' ? -1 : undefined).join(':'), kararaGit: p[p.length - 1] === 'karar', pf: 'hepsi' };
  if (k === 'pord') return sekmeAc({ ...d, sip: { ...d.sip, f: 'hepsi', ...bos, who: p.slice(1).join(':') } }, 'siparisler');
  return d;
}

// ---------------------------------------------------------------------------
// Adres çubuğu
// ---------------------------------------------------------------------------

const SEKMELER: readonly Sekme[] = ['genel', 'talepler', 'uyeler', 'siparisler', 'odemeler'];

/** Eski sekmelerin yeni yerleri (Ürün Sahibi, 07.10.2026). `null`: bu sayfada kalır. */
export const ESKI_YONLENDIRME: Readonly<Record<string, { yol: string } | { sekme: string; f?: string }>> = {
  'satis-raporu': { yol: '/satis' },
  satis: { yol: '/satis' },
  katalog: { yol: '/satis' },
  alacaklar: { yol: '/finans' },
  finans: { yol: '/finans' },
  operasyon: { sekme: 'siparisler', f: 'rez' },
  'web-kayitlari': { sekme: 'uyeler' },
};

/** `?sekme=siparisler&f=rez&q=...` -> durum. Tanınmayan değer sessizce atılır, varsayılan kalır. */
export function urldenDurum(arama: string): Durum {
  const q = new URLSearchParams(arama);
  let d = ilkDurum();
  const eski = ESKI_YONLENDIRME[q.get('sekme') ?? ''];
  const sekme = eski && 'sekme' in eski ? eski.sekme : (q.get('sekme') ?? 'genel');
  const f = eski && 'f' in eski && eski.f ? eski.f : (q.get('f') ?? '');
  if (!SEKMELER.includes(sekme as Sekme)) return d;
  d = { ...d, sekme: sekme as Sekme };
  const kisi = q.get('kisi');
  if (kisi && sekme === 'uyeler') d = { ...d, kisi };
  const ortak = { q: q.get('q') ?? '', from: q.get('from') ?? '', to: q.get('to') ?? '' };
  if (sekme === 'odemeler') {
    const status = q.get('status') ?? '';
    const offset = Number(q.get('offset') ?? 0);
    d.odeme = {
      reference: q.get('reference') ?? '', from: ortak.from, to: ortak.to,
      status: ['CREATED', 'AUTHORIZED', 'CAPTURED', 'FAILED', 'REFUNDED', 'VOIDED'].includes(status) ? status as WebPaymentStatus : '',
      notification: q.get('notification') === 'failed' ? 'failed' : '',
      offset: Number.isSafeInteger(offset) && offset >= 0 && offset <= 1_000_000 ? offset : 0,
    };
  }
  if (sekme === 'talepler') {
    const tab = q.get('tab');
    const tur = q.get('tur');
    d = {
      ...d,
      talep: {
        ...d.talep,
        ...ortak,
        tab: tab === 'musteri' || tab === 'kapali' ? tab : 'sende',
        tur: ['destek', 'on', 'vize', 'onvize', 'sistem', 'spam'].includes(tur ?? '') ? (tur as TalepSuzgeci['tur']) : 'hepsi',
        over: q.get('over') === '1',
        sort: q.get('sort') === 'yeni' ? 'yeni' : 'yas',
      },
    };
  } else if (sekme === 'uyeler') {
    const uf = ['dogrulanmamis', 'dogrulanmis', 'oturumsuz', 'siparisli', 'kayip-odemeli'].includes(f) ? (f as UyeSuzgeci['f']) : 'hepsi';
    d = { ...d, uye: { ...d.uye, ...ortak, f: uf, sort: q.get('sort') === 'seen' ? 'seen' : 'reg' } };
  } else if (sekme === 'siparisler') {
    const sf = ['bekleyen', 'rez', 'geciken', 'odendi', 'iptal'].includes(f) ? (f as SiparisSuzgeci['f']) : 'hepsi';
    d = { ...d, sip: { ...d.sip, ...ortak, f: sf, who: q.get('who') } };
  }
  return d;
}

/** Durum -> `?sekme=...`. Yalnız varsayılandan farklı olanlar yazılır: paylaşılan adres kısa kalır. */
export function durumdanUrl(d: Durum): string {
  const q = new URLSearchParams();
  if (d.sekme !== 'genel') q.set('sekme', d.sekme);
  const ortak = (o: { q: string; from: string; to: string }) => {
    if (o.q) q.set('q', o.q);
    if (o.from) q.set('from', o.from);
    if (o.to) q.set('to', o.to);
  };
  if (d.sekme === 'uyeler' && d.kisi) q.set('kisi', d.kisi);
  else if (d.sekme === 'talepler') {
    if (d.talep.tab !== 'sende') q.set('tab', d.talep.tab);
    if (d.talep.tur !== 'hepsi') q.set('tur', d.talep.tur);
    if (d.talep.over) q.set('over', '1');
    if (d.talep.sort !== 'yas') q.set('sort', d.talep.sort);
    ortak(d.talep);
  } else if (d.sekme === 'uyeler') {
    if (d.uye.f !== 'hepsi') q.set('f', d.uye.f);
    if (d.uye.sort !== 'reg') q.set('sort', d.uye.sort);
    ortak(d.uye);
  } else if (d.sekme === 'siparisler') {
    if (d.sip.f !== 'hepsi') q.set('f', d.sip.f);
    if (d.sip.who) q.set('who', d.sip.who);
    ortak(d.sip);
  }
  if (d.sekme === 'odemeler') {
    for (const key of ['reference', 'status', 'notification', 'from', 'to'] as const) if (d.odeme[key]) q.set(key, d.odeme[key]);
    if (d.odeme.offset) q.set('offset', String(d.odeme.offset));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}
